/**
 * Cloud File Breadcrumbs Component
 *
 * Displays a breadcrumb navigation trail for cloud file paths.
 * Includes home and parent navigation buttons.
 */

import React from "react";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FolderIcon from "@mui/icons-material/Folder";
import {
  splitPath,
  buildPathToIndex,
  getParentPath,
  isRootPath,
} from "../utils/pathUtils";

interface CloudFileBreadcrumbsProps {
  /** Current path being displayed */
  currentPath: string;
  /** Callback when a breadcrumb segment is clicked */
  onNavigate: (path: string) => void;
  /** Maximum segments to show before collapsing (default: 5) */
  maxItems?: number;
  /** Show home button (default: true) */
  showHomeButton?: boolean;
  /** Show go-to-parent button (default: true) */
  showParentButton?: boolean;
}

export const CloudFileBreadcrumbs: React.FC<CloudFileBreadcrumbsProps> = ({
  currentPath,
  onNavigate,
  maxItems = 5,
  showHomeButton = true,
  showParentButton = true,
}) => {
  const segments = splitPath(currentPath);
  const isRoot = isRootPath(currentPath);

  const handleHomeClick = () => {
    onNavigate("/");
  };

  const handleParentClick = () => {
    if (!isRoot) {
      onNavigate(getParentPath(currentPath));
    }
  };

  const handleSegmentClick = (index: number) => {
    const path = buildPathToIndex(segments, index);
    onNavigate(path);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        py: 1,
        px: 2,
        bgcolor: "grey.50",
        borderBottom: 1,
        borderColor: "divider",
        minHeight: 40,
      }}
      data-testid="cloud-breadcrumbs"
    >
      {/* Home button */}
      {showHomeButton && (
        <Tooltip title="Go to root">
          <span>
            <IconButton
              size="small"
              onClick={handleHomeClick}
              disabled={isRoot}
              sx={{ color: isRoot ? "text.disabled" : "primary.main" }}
              data-testid="cloud-breadcrumb-home"
              data-attr="datasets:btn:cloud-navigate-home"
            >
              <HomeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Parent button */}
      {showParentButton && (
        <Tooltip title="Go to parent folder">
          <span>
            <IconButton
              size="small"
              onClick={handleParentClick}
              disabled={isRoot}
              sx={{ color: isRoot ? "text.disabled" : "text.secondary" }}
              data-testid="cloud-breadcrumb-parent"
              data-attr="datasets:btn:cloud-navigate-parent"
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Breadcrumb trail */}
      <Breadcrumbs
        separator={
          <NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />
        }
        maxItems={maxItems}
        itemsBeforeCollapse={1}
        itemsAfterCollapse={2}
        sx={{ flex: 1, ml: 1 }}
      >
        {/* Root item */}
        <Link
          component="button"
          underline="hover"
          color={segments.length === 0 ? "text.primary" : "inherit"}
          onClick={handleHomeClick}
          data-testid="cloud-breadcrumb-root"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            border: "none",
            bgcolor: "transparent",
            fontSize: "0.875rem",
          }}
        >
          <FolderIcon fontSize="small" sx={{ color: "#f9a825" }} />
          Root
        </Link>

        {/* Path segments */}
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;

          if (isLast) {
            return (
              <Typography
                key={index}
                color="text.primary"
                variant="body2"
                sx={{ fontWeight: 500 }}
                data-testid={`cloud-breadcrumb-${segment}`}
              >
                {segment}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component="button"
              underline="hover"
              color="inherit"
              onClick={() => handleSegmentClick(index)}
              data-testid={`cloud-breadcrumb-${segment}`}
              sx={{
                cursor: "pointer",
                border: "none",
                bgcolor: "transparent",
                fontSize: "0.875rem",
              }}
            >
              {segment}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default CloudFileBreadcrumbs;
